import React, { useState, useEffect } from 'react';
import GalleryItem from './GalleryItem';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RecentCreations - Dashboard section showing user's recent generated content
 */
const RecentCreations = ({ token, onViewAll, onViewItem }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecentItems();
  }, [token]);

  const fetchRecentItems = async () => {
    if (!token) {
      setLoading(false);
      setItems([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/gallery/recent?limit=8`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        // Token might be invalid/expired - just show empty state
        setItems([]);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch recent items');
      }

      const data = await response.json();
      setItems(data.items || []);
    } catch (err) {
      console.error('Error fetching recent creations:', err);
      // Don't show error for network issues, just show empty state
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <span className="text-purple-500">🖼️</span> Recent Creations
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <span className="text-purple-500">🖼️</span> Recent Creations
          </h3>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-center py-4">
          Unable to load recent creations
        </p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <span className="text-purple-500">🖼️</span> Recent Creations
          </h3>
        </div>
        <div className="text-center py-8">
          <div className="text-4xl mb-3">🎨</div>
          <p className="text-gray-500 dark:text-gray-400">
            No creations yet. Start generating content in the Ad Creator Hub!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
      data-testid="recent-creations-section"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
          <span className="text-purple-500">🖼️</span> Recent Creations
        </h3>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium flex items-center gap-1 transition-colors"
            data-testid="view-all-gallery-btn"
          >
            View All
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.slice(0, 8).map((item) => (
          <GalleryItem
            key={item.id}
            item={item}
            size="small"
            showActions={false}
            onView={onViewItem}
          />
        ))}
      </div>

      {items.length > 0 && (
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">
            Showing {Math.min(items.length, 8)} of your recent creations
          </p>
        </div>
      )}
    </div>
  );
};

export default RecentCreations;
